import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import ReplyIcon from '@mui/icons-material/Reply';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import CustomToast from '../../components/Redesing/custom-toast';
import {
  ChatAttachmentRejected,
  ChatMessageActionInput,
  ChatMessageAttachmentInput,
} from '../../hooks/chat/api';
import { MediaUploadCanceled, uploadChatMedia } from '../../hooks/chat/media';
import { useChatMediaUploads } from '../../hooks/chat/useChatMediaUploads';
import {
  acceptedTypes,
  formatDuration,
  MediaKind,
  mediaFileError,
  mediaKindOf,
  probeVideo,
  readAsDataUrl,
  readImageSize,
  reportedDuration,
  videoLengthError,
} from './attachments';
import { ActionsMenuButton, DraftActionChips } from './MessageActions';
import { AntChatSavedReply } from '../../hooks/chat/api';
import { useSavedReplies } from '../../hooks/chat/useSavedReplies';
import { ManageSavedRepliesDialog } from './ManageSavedRepliesDialog';
import {
  SavedReplyButton,
  SlashReplyMenu,
  moveIndex,
  slashMatches,
} from './SavedReplyPicker';
import { fillReplyVariables, firstNameOf, slashQuery } from './savedReplies';
import {
  clearStoredDraft,
  pruneStoredDrafts,
  readStoredDraft,
  writeStoredDraft,
} from './draftStorage';
import { useAdminStore } from '../../store/adminStore';

export interface SendOptions {
  note?: boolean;
  /** A legacy photo, base64 (media uploads off). */
  image?: string | null;
  /** A photo or video already in the chat media bucket (media uploads on). */
  attachment?: ChatMessageAttachmentInput;
  /** Buttons under the reply (never on a note); absent when there are none. */
  actions?: ChatMessageActionInput[];
}

/** The file attached to the draft reply. */
export type Draft =
  | { via: 'legacy'; name: string; dataUrl: string }
  | {
      via: 'bucket';
      kind: MediaKind;
      name: string;
      file: File;
      /** An object URL of the photo, or of the video's poster; revoked with the draft. */
      previewUrl: string | null;
      durationMs: number | null;
      width: number | null;
      height: number | null;
      poster: Blob | null;
      /** Once the bucket holds the file: a send that fails after it does not upload again. */
      uploaded?: ChatMessageAttachmentInput;
    };

const errorText = (error: unknown) =>
  error instanceof Error ? error.message : String(error);

/** The preview's object URL goes with the draft it belongs to, not before. */
const revokePreview = (draft: Draft | null | undefined) => {
  if (draft?.via === 'bucket' && draft.previewUrl) URL.revokeObjectURL(draft.previewUrl);
};

/** What the agent had in the composer for a conversation they left. */
export interface ComposerDraft {
  /** The words in the tab on screen (`mode`'s). */
  input: string;
  mode: 'reply' | 'note';
  /**
   * The other tab's words, when it has its own: a reply kept out of sight
   * while the agent writes a note on a chat another agent has.
   */
  aside?: string;
  actions: ChatMessageActionInput[];
  attachment: Draft | null;
}

/**
 * Drafts kept across conversations, including sends awaiting confirmation,
 * by session id. In memory, and the source of truth; localStorage (draftStorage) keeps a
 * copy of the text, mode and buttons so a reload brings them back, and a
 * composer that finds nothing here reads it. Nothing uploads in the
 * background; a file left mid-upload is uploaded again when its reply is sent.
 */
export interface ComposerDrafts {
  adminId: string;
  map: Map<string, ComposerDraft>;
  /** A remounted composer waits for the same send before it offers another. */
  pending: Map<string, { kind: 'reply' | 'note'; done: Promise<void> }>;
  /** Drops every draft, with the previews they held. */
  dispose: () => void;
}

export const createComposerDrafts = (
  adminId = useAdminStore.getState().admin.admin?.id ?? ''
): ComposerDrafts => {
  const map = new Map<string, ComposerDraft>();
  const pending: ComposerDrafts['pending'] = new Map();
  return {
    adminId,
    map,
    pending,
    dispose: () => {
      map.forEach((draft) => revokePreview(draft.attachment));
      map.clear();
      pending.clear();
    },
  };
};

/**
 * One draft store for as long as the page lives: a composer that remounts per
 * conversation finds in it what it wrote in the others.
 */
export function useComposerDrafts(): ComposerDrafts {
  const adminId = useAdminStore((state) => state.admin.admin?.id ?? '');
  const drafts = useMemo(() => createComposerDrafts(adminId), [adminId]);
  useEffect(() => {
    // Drafts nobody came back for in a week go, once per visit.
    pruneStoredDrafts();
    return () => drafts.dispose();
  }, [drafts]);
  return drafts;
}

/**
 * What a reload left in storage for the conversation, as a draft: text, mode
 * and buttons; the file is not kept, and the agent attaches it again.
 */
const storedDraft = (sessionId: string, adminId: string): ComposerDraft | null => {
  const stored = readStoredDraft(sessionId, undefined, adminId);
  return stored && { ...stored, attachment: null };
};

/** Each tab's words, from a draft that keeps them as the tab on screen's and the other's. */
const textsOf = (draft: Pick<ComposerDraft, 'input' | 'mode' | 'aside'>) =>
  draft.mode === 'note'
    ? { replyText: draft.aside ?? '', noteText: draft.input }
    : { replyText: draft.input, noteText: draft.aside ?? '' };

/** The other way round: the tab on screen's words, and the other tab's when it has any. */
const draftTexts = (
  replyText: string,
  noteText: string,
  mode: ComposerDraft['mode']
): Pick<ComposerDraft, 'input' | 'aside'> => {
  const [input, aside] = mode === 'note' ? [noteText, replyText] : [replyText, noteText];
  return aside ? { input, aside } : { input };
};

/**
 * ant refused the attachment without reaching the bucket ("could not be
 * checked with the bucket, send again", AttachMedia's unavailable case): the
 * object uploaded is still good, so the next send only sends. The rider app
 * matches the same words; ant keeps them.
 */
const bucketNotChecked = (error: unknown) =>
  errorText(error).toLowerCase().includes('could not be checked');

interface Props {
  /**
   * The session (ant's ChatSession id). Drafts belong to it: switching puts
   * one away and brings the other's back. Photos and video upload into it:
   * ant gives an agent a slot only for an open session they may send into.
   */
  conversationId: string;
  onSend: (content: string, options: SendOptions) => Promise<void>;
  /**
   * Where the other conversations' drafts wait (useComposerDrafts). Without
   * it the composer keeps its own, which last as long as it does.
   */
  drafts?: ComposerDrafts;
  /** The rental the chat is about: rental buttons start with it. */
  anchoredRentalId?: string | null;
  /** The rider's ant user id: rental buttons pick from their rentals. */
  riderId?: string | null;
  /** Called as the agent types a reply (not a note), for the typing indicator. */
  onTyping?: () => void;
  /** The session's system: saved replies offered are its own and the global ones. */
  systemId?: string | null;
  systemName?: string | null;
  /** Fill {{rider_first_name}} and {{agent_first_name}} in saved replies. */
  riderName?: string | null;
  agentName?: string | null;
  /** A Movatic admin may write saved replies for every system. */
  canManageGlobal?: boolean;
  /**
   * The name of the agent who has this chat, when it is someone else
   * (assignee.ts): the Reply tab says so, with Take over, in place of the
   * reply and what goes with it (its photo or video, its buttons). Internal
   * notes stay, with words of their own. Null or absent: the agent replies
   * as usual.
   */
  heldBy?: string | null;
  /** Makes the chat this agent's; rejects (after the page said why) when it could not. */
  onTakeOver?: () => Promise<void>;
}

/**
 * Reply to the rider or add an internal note. Enter sends, Shift+Enter is a
 * new line. A reply may carry one photo or video with the text as its
 * caption, and up to 4 buttons the rider can tap (under text: ant refuses a
 * reply without a body or an image); a note is text only and never reaches
 * the rider. Saved replies come from the button or from "/" at the start of
 * the text, with the rider's and agent's first names filled in.
 *
 * With ant's media bucket on, a photo (PNG/JPEG, 10 MB) or video (MP4/MOV,
 * 50 MB, 60 s) is uploaded when the reply is sent (slot, PUT with progress,
 * then the message), and the chip cancels an upload under way. Off, a photo
 * (2 MB) goes base64 with the message, and there is no video.
 *
 * Leaving a conversation puts its draft away (text, mode, buttons, attachment
 * and its thumbnail); coming back brings it back. An upload under way when
 * the agent leaves stops, and Send uploads the file again on their return.
 * The text, mode and buttons are also mirrored to localStorage as the agent
 * writes, so a reload brings them back too (not the file: it is attached
 * again); sending, or emptying the draft, clears the copy.
 *
 * On a chat another agent has (`heldBy`), the Reply tab holds who has it and
 * Take over instead of the reply; whatever reply was drafted waits, unseen,
 * and is back once the chat is this agent's. The Internal note tab works as
 * ever, from its own words: helping a colleague is not taking the chat, and
 * the reply put aside never lands in a note. A reply still uploading its
 * file when the chat changes hands does not go (the file stays uploaded,
 * so Send after a Take over only sends). An agent typing as it changes
 * hands types into the bar, which holds the keys from the page's shortcuts;
 * Take over gives them the reply box back, focused.
 */
export function Composer(props: Props) {
  const adminId = useAdminStore((state) => state.admin.admin?.id ?? '');
  // Account changes reset every piece of composer state, including files and uploads.
  return adminId ? <AccountComposer key={adminId} {...props} adminId={adminId} /> : null;
}

function AccountComposer({
  adminId,
  conversationId,
  onSend,
  drafts: sharedDrafts,
  anchoredRentalId,
  riderId,
  onTyping,
  systemId = null,
  systemName = null,
  riderName = null,
  agentName = null,
  canManageGlobal = false,
  heldBy = null,
  onTakeOver,
}: Props & { adminId: string }) {
  /** The reply's words and the note's: each tab keeps its own (see switchMode). */
  const [replyText, setReplyText] = useState('');
  const [noteText, setNoteText] = useState('');
  const [mode, setMode] = useState<'reply' | 'note'>('reply');
  /** What is on its way, from the click until ant answers: the reply or the note. */
  const [sendingKind, setSendingKind] = useState<'reply' | 'note' | null>(null);
  const sending = sendingKind !== null;
  const [attachment, setAttachment] = useState<Draft | null>(null);
  /** Reading a video's length (or a photo's size) before it is attached. */
  const [preparing, setPreparing] = useState(false);
  /** 0 to 1 while the attachment uploads; null otherwise. */
  const [progress, setProgress] = useState<number | null>(null);
  const [actions, setActions] = useState<ChatMessageActionInput[]>([]);
  /** Bumped per conversation: work begun for another one leaves this draft alone. */
  const generationRef = useRef(0);
  /** A file still being read when the composer goes makes no preview to leak. */
  const mountedRef = useRef(true);
  const uploadRef = useRef<AbortController | null>(null);
  const mediaUploads = useChatMediaUploads(conversationId);
  const bucket = mediaUploads;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement | null>(null);
  const [rowEl, setRowEl] = useState<HTMLDivElement | null>(null);
  const [manageOpen, setManageOpen] = useState(false);
  /** Esc closed the "/" list; it opens again once the text stops starting with "/". */
  const [slashDismissed, setSlashDismissed] = useState(false);
  const [slashIndex, setSlashIndex] = useState(0);
  const savedReplies = useSavedReplies(systemId);

  const ownDrafts = useRef<ComposerDrafts | null>(null);
  if (!ownDrafts.current) ownDrafts.current = createComposerDrafts(adminId);
  const drafts = sharedDrafts?.adminId === adminId ? sharedDrafts : ownDrafts.current;
  /** The draft as it is now, for the moment the conversation is left. */
  const latest = useRef({
    replyText,
    noteText,
    mode,
    actions,
    attachment,
  });
  latest.current = {
    replyText,
    noteText,
    mode,
    actions,
    attachment,
  };
  const restoreDraft = useCallback((saved: ComposerDraft) => {
    const texts = textsOf(saved);
    setReplyText(texts.replyText);
    setNoteText(texts.noteText);
    setMode(saved.mode);
    setActions(saved.actions);
    setAttachment(saved.attachment);
    // Also ready for cleanup before the state update commits (StrictMode).
    latest.current = {
      ...latest.current,
      ...texts,
      mode: saved.mode,
      actions: saved.actions,
      attachment: saved.attachment,
    };
  }, []);
  /**
   * The conversation the text, mode and buttons belong to: null between
   * leaving one and having brought the next one's back, when the state on
   * screen is still the last conversation's and must not be mirrored under
   * the new id. Set by the effect below, which runs after the mirror.
   */
  const ownerRef = useRef<string | null>(null);

  // The copy a reload reads: written as the draft changes, dropped once it
  // is empty. Declared before the effect that switches conversations, so
  // that on the render that switches, this one runs (if at all) while the
  // owner is still unset.
  useEffect(() => {
    if (ownerRef.current !== conversationId) return;
    const saved = {
      ...draftTexts(replyText, noteText, mode),
      mode,
      actions,
      attachment,
    };
    if (drafts.pending.has(conversationId)) drafts.map.set(conversationId, saved);
    writeStoredDraft(conversationId, saved, undefined, adminId);
  }, [conversationId, replyText, noteText, mode, actions, attachment, drafts, adminId]);

  useEffect(() => {
    // What the page put away, else what a reload left in storage.
    const saved = drafts.map.get(conversationId) ?? storedDraft(conversationId, adminId);
    const pending = drafts.pending.get(conversationId);
    if (saved) {
      if (!pending) drafts.map.delete(conversationId);
      restoreDraft(saved);
    }
    if (pending) {
      const generation = generationRef.current;
      setSendingKind(pending.kind);
      pending.done.then(() => {
        if (generation !== generationRef.current) return;
        const settled = drafts.map.get(conversationId);
        if (settled) restoreDraft(settled);
        drafts.map.delete(conversationId);
        setSendingKind(null);
      });
    }
    ownerRef.current = conversationId;
    return () => {
      // Leaving this conversation, for another or because the composer goes.
      ownerRef.current = null;
      generationRef.current += 1;
      // An upload under way stops; the file stays in the draft, and Send
      // uploads it again (the bucket signs a new object).
      uploadRef.current?.abort();
      uploadRef.current = null;
      const draft = latest.current;
      if (useAdminStore.getState().admin.admin?.id !== adminId) {
        revokePreview(draft.attachment);
        drafts.dispose();
        return;
      }
      // An in-flight request is still a draft until ant confirms success.
      const empty =
        !draft.replyText &&
        !draft.noteText &&
        draft.actions.length === 0 &&
        !draft.attachment &&
        draft.mode === 'reply';
      if (empty) {
        drafts.map.delete(conversationId);
        clearStoredDraft(conversationId, adminId);
      } else {
        const kept = {
          ...draftTexts(draft.replyText, draft.noteText, draft.mode),
          mode: draft.mode,
          actions: draft.actions,
        };
        drafts.map.set(conversationId, { ...kept, attachment: draft.attachment });
        writeStoredDraft(conversationId, kept, undefined, adminId);
      }
      setReplyText('');
      setNoteText('');
      setAttachment(null);
      setActions([]);
      setMode('reply');
      setSendingKind(null);
      setPreparing(false);
      setProgress(null);
    };
  }, [conversationId, drafts, restoreDraft, adminId]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // After the draft above is put away: a composer with its own store drops
  // them all with itself, and nothing that store held leaks.
  useEffect(() => () => ownDrafts.current?.dispose(), []);

  // Another agent has the chat: no reply goes from here, nor its file or buttons.
  const replyHeld = heldBy !== null && mode === 'reply';
  /** Who has the chat as of this render: a send that resumes after its upload asks again. */
  const heldByRef = useRef(heldBy);
  heldByRef.current = heldBy;
  const isNote = mode === 'note';
  /** The words in the box on screen: the reply's, or the note's. */
  const input = isNote ? noteText : replyText;
  const setInput = isNote ? setNoteText : setReplyText;

  const barRef = useRef<HTMLDivElement | null>(null);
  /** Take over was clicked here: the reply box takes the focus once it is back. */
  const takingOverRef = useRef(false);
  /**
   * Whether the last commit showed the bar in place of the reply row. On
   * the commit where the chat goes to another agent both are on the page,
   * so the effect below still finds the focus in the reply box and hands it
   * to the bar before the row goes: a focused box that leaves the page
   * drops the focus on <body>, where the rest of what the agent types would
   * be the page's shortcuts (n opens another rider's chat, e ends one).
   */
  const [shownHeld, setShownHeld] = useState(replyHeld);
  useLayoutEffect(() => {
    if (shownHeld === replyHeld) return;
    const active = document.activeElement;
    if (replyHeld) {
      if (active && active === textRef.current) barRef.current?.focus();
    } else {
      // Taken over from the bar, whose button went with it: back to the reply.
      if (takingOverRef.current && (!active || active === document.body)) {
        textRef.current?.focus();
      }
      takingOverRef.current = false;
    }
    setShownHeld(replyHeld);
  }, [replyHeld, shownHeld]);
  const showRow = !replyHeld || !shownHeld;

  const slash = slashQuery(input);
  const slashOpen = slash !== null && !slashDismissed && !replyHeld;
  const matches = slashMatches(savedReplies.replies, slashOpen ? slash : null);
  const { ensure: ensureReplies } = savedReplies;
  useEffect(() => {
    if (slash === null) setSlashDismissed(false);
    setSlashIndex(0);
  }, [slash]);
  useEffect(() => {
    if (slashOpen) ensureReplies();
  }, [slashOpen, ensureReplies]);

  /** The reply's text in the composer: in place of "/…", or after what is there. */
  const insertReply = (reply: AntChatSavedReply, replace: boolean) => {
    const text = fillReplyVariables(reply.body, {
      riderFirstName: firstNameOf(riderName),
      agentFirstName: firstNameOf(agentName),
    });
    const next = replace || !input.trim() ? text : `${input.trimEnd()} ${text}`;
    setInput(next);
    setSlashDismissed(false);
    window.setTimeout(() => {
      const el = textRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(next.length, next.length);
    }, 0);
  };

  const hasText = Boolean(input.trim());
  /** ant refuses a reply with no body and no image, and buttons need words above them. */
  const buttonsNeedText = !isNote && actions.length > 0 && !hasText;
  const canSend =
    !replyHeld &&
    !sending &&
    !preparing &&
    !buttonsNeedText &&
    (hasText || (!isNote && Boolean(attachment)));

  const send = async () => {
    if (!canSend || drafts.pending.has(conversationId)) return;
    const generation = generationRef.current;
    const draft = isNote ? null : attachment;
    const note = isNote;
    let finish!: () => void;
    const pending = {
      kind: mode,
      done: new Promise<void>((resolve) => {
        finish = resolve;
      }),
    };
    drafts.pending.set(conversationId, pending);
    drafts.map.set(conversationId, {
      ...draftTexts(replyText, noteText, mode),
      mode,
      actions,
      attachment,
    });
    let uploading = false;
    setSendingKind(note ? 'note' : 'reply');
    try {
      let uploaded: ChatMessageAttachmentInput | undefined;
      if (draft?.via === 'bucket') {
        uploaded = draft.uploaded;
        if (!uploaded) {
          uploading = true;
          const controller = new AbortController();
          uploadRef.current = controller;
          setProgress(0);
          uploaded = await uploadChatMedia({
            sessionId: conversationId,
            file: draft.file,
            kind: draft.kind,
            durationMs: draft.durationMs,
            width: draft.width,
            height: draft.height,
            poster: draft.poster,
            signal: controller.signal,
            onProgress: (fraction) => {
              if (!controller.signal.aborted) setProgress(fraction);
            },
          });
          // The cross clicked as the upload settled: the file is gone, nothing goes.
          if (uploadRef.current !== controller) return;
          uploading = false;
          uploadRef.current = null;
          const done = uploaded;
          setAttachment((prev) => (prev === draft ? { ...draft, uploaded: done } : prev));
          setProgress(null);
        }
      }
      // The chat went to another agent while the file uploaded: the reply
      // waits for a Take over with its file uploaded, and Send then only sends.
      const holder = heldByRef.current;
      if (!note && holder !== null) {
        CustomToast({
          type: 'error',
          message: `Not sent: ${holder} has this chat now. Take over to send it.`,
          duration: 6000,
        });
        return;
      }
      if (useAdminStore.getState().admin.admin?.id !== adminId) return;
      await onSend(input.trim(), {
        note,
        image: note ? null : draft?.via === 'legacy' ? draft.dataUrl : undefined,
        ...(uploaded ? { attachment: uploaded } : {}),
        ...(!note && actions.length > 0 ? { actions } : {}),
      });
      if (useAdminStore.getState().admin.admin?.id !== adminId) return;
      const saved =
        drafts.map.get(conversationId) ?? storedDraft(conversationId, adminId);
      if (saved) {
        const texts = textsOf(saved);
        const kept: ComposerDraft = {
          ...draftTexts(
            note ? texts.replyText : '',
            note ? '' : texts.noteText,
            saved.mode
          ),
          mode: saved.mode,
          actions: note ? saved.actions : [],
          attachment: note ? saved.attachment : null,
        };
        drafts.map.set(conversationId, kept);
        writeStoredDraft(conversationId, kept, undefined, adminId);
        if (generation === generationRef.current) restoreDraft(kept);
      }
      if (!note) revokePreview(draft);
    } catch (error) {
      if (error instanceof MediaUploadCanceled) return;
      const rejected = error instanceof ChatAttachmentRejected && draft?.via === 'bucket';
      // Missing, mismatched or foreign object: the next send uploads again.
      // ant not reaching the bucket is not about the object: it stays.
      const uploadAgain = rejected && !bucketNotChecked(error);
      if (uploadAgain) {
        const saved = drafts.map.get(conversationId);
        if (saved?.attachment?.via === 'bucket') {
          drafts.map.set(conversationId, {
            ...saved,
            attachment: { ...saved.attachment, uploaded: undefined },
          });
        }
        if (generation === generationRef.current) {
          setAttachment((prev) =>
            prev?.via === 'bucket' ? { ...prev, uploaded: undefined } : prev
          );
        }
      }
      const advice = !rejected
        ? ''
        : uploadAgain
        ? `. Send again to upload the ${draft?.kind} again.`
        : `. The ${draft?.kind} is still uploaded: send again.`;
      CustomToast({
        type: 'error',
        message: uploading
          ? `Could not upload ${draft?.name}. ${errorText(error)}`
          : `Could not ${note ? 'add the note' : 'send the message'}. ${errorText(
              error
            )}${advice}`,
        duration: 6000,
      });
    } finally {
      drafts.pending.delete(conversationId);
      finish();
      if (generation === generationRef.current) {
        uploadRef.current = null;
        setSendingKind(null);
        setProgress(null);
      }
    }
  };

  /** The chip's cross: drops the attachment, stopping its upload if one is under way. */
  const removeAttachment = () => {
    uploadRef.current?.abort();
    uploadRef.current = null;
    revokePreview(attachment);
    setAttachment(null);
  };

  /** A new file in place of the one attached (if any), whose preview goes with it. */
  const replaceAttachment = useCallback((next: Draft) => {
    revokePreview(latest.current.attachment);
    setAttachment(next);
  }, []);

  /**
   * The other tab. Words begun on one tab go along to the other while it
   * has none of its own (a note begun on the Reply tab), as they always
   * have; but on a chat another agent has, the reply stays where it is, out
   * of sight, and the note starts from its own words: nothing meant for the
   * rider shows up in a note, or goes out as one.
   */
  const switchMode = (next: 'reply' | 'note') => {
    if (next === mode) return;
    const from = mode === 'note' ? noteText : replyText;
    const to = next === 'note' ? noteText : replyText;
    if (heldBy === null && !sending && from && !to) {
      setReplyText(next === 'reply' ? from : '');
      setNoteText(next === 'note' ? from : '');
    }
    setMode(next);
  };

  /** Take over from the bar: the reply box takes the focus when it is back. */
  const takeOver = async () => {
    takingOverRef.current = true;
    try {
      await onTakeOver?.();
    } catch (error) {
      takingOverRef.current = false;
      throw error;
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (slashOpen && !e.nativeEvent.isComposing) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setSlashDismissed(true);
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey && matches.length === 0) {
        // No match yet (or the replies are still loading): never send "/query".
        e.preventDefault();
        return;
      }
      if (matches.length > 0) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          setSlashIndex((i) =>
            moveIndex(i, e.key === 'ArrowDown' ? 1 : -1, matches.length)
          );
          return;
        }
        if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
          e.preventDefault();
          insertReply(matches[Math.min(slashIndex, matches.length - 1)], true);
          return;
        }
      }
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };

  const onFile = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (!file) return;
      const refuse = (message: string) =>
        CustomToast({ type: 'error', message, duration: 6000 });
      const problem = mediaFileError(file, bucket);
      if (problem) {
        refuse(problem);
        return;
      }
      const generation = generationRef.current;
      const current = () => mountedRef.current && generation === generationRef.current;

      if (!bucket) {
        try {
          const dataUrl = await readAsDataUrl(file);
          if (current()) replaceAttachment({ via: 'legacy', name: file.name, dataUrl });
        } catch (_) {
          CustomToast({ type: 'error', message: 'Could not read that file.' });
        }
        return;
      }

      const kind = mediaKindOf(file.type) as MediaKind;
      setPreparing(true);
      try {
        if (kind === 'photo') {
          const size = await readImageSize(file);
          if (!current()) return;
          replaceAttachment({
            via: 'bucket',
            kind,
            name: file.name,
            file,
            previewUrl: URL.createObjectURL(file),
            durationMs: null,
            width: size?.width ?? null,
            height: size?.height ?? null,
            poster: null,
          });
          return;
        }
        let video;
        try {
          video = await probeVideo(file);
        } catch (_) {
          if (current()) {
            refuse(
              `Could not read ${file.name} in this browser, so its length cannot be checked. Try an MP4.`
            );
          }
          return;
        }
        if (!current()) return;
        const tooLong = videoLengthError(file.name, video.durationMs);
        if (tooLong) {
          refuse(tooLong);
          return;
        }
        replaceAttachment({
          via: 'bucket',
          kind,
          name: file.name,
          file,
          previewUrl: video.poster ? URL.createObjectURL(video.poster) : null,
          durationMs: reportedDuration(video.durationMs),
          width: video.width,
          height: video.height,
          poster: video.poster,
        });
      } finally {
        if (current()) setPreparing(false);
      }
    },
    [bucket, replaceAttachment]
  );

  const uploadPercent = progress === null ? null : Math.round(progress * 100);

  return (
    <Box
      sx={(theme) => ({
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: isNote ? alpha(theme.palette.warning.main, 0.08) : 'background.paper',
        transition: 'background-color 0.15s',
      })}
      data-testid="composer"
    >
      {/* The note colour is set from the Tabs: an sx on one Tab gives it its
          own class, which breaks the theme's "& + &" gap between tabs. */}
      <Tabs
        value={mode}
        onChange={(_, v) => switchMode(v)}
        sx={{
          minHeight: 36,
          px: 1.5,
          '& .MuiTab-root': { minHeight: 36, py: 0 },
          '& .MuiTab-root + .MuiTab-root': { ml: 3 },
          ...(isNote ? { '& .MuiTab-root.Mui-selected': { color: 'warning.dark' } } : {}),
        }}
        textColor={isNote ? 'inherit' : 'primary'}
        TabIndicatorProps={{ sx: { bgcolor: isNote ? 'warning.main' : 'primary.main' } }}
      >
        <Tab
          value="reply"
          label="Reply"
          icon={<ReplyIcon fontSize="small" />}
          iconPosition="start"
        />
        <Tab
          value="note"
          label="Internal note"
          icon={<StickyNote2OutlinedIcon fontSize="small" />}
          iconPosition="start"
        />
      </Tabs>

      {isNote && (
        <Typography
          variant="caption"
          sx={{ px: 2, color: 'warning.dark', display: 'block' }}
        >
          Only the support team sees internal notes. The rider is not notified.
        </Typography>
      )}

      {replyHeld && (
        <HeldByBar
          ref={barRef}
          name={heldBy}
          onTakeOver={onTakeOver ? takeOver : undefined}
        />
      )}

      {!isNote && !replyHeld && (
        <DraftActionChips
          actions={actions}
          disabled={sending}
          onRemove={(index) => setActions((prev) => prev.filter((_, i) => i !== index))}
        />
      )}

      {buttonsNeedText && !replyHeld && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ px: 2, pt: 0.5, display: 'block' }}
          data-testid="buttons-need-text"
        >
          Add a message above the buttons
        </Typography>
      )}

      {/* Held mid-upload, the chip stays until the upload ends: its cross stops it. */}
      {attachment && !isNote && (!replyHeld || uploadPercent !== null) && (
        <Box sx={{ px: 2, pt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachmentThumb draft={attachment} />
            <Chip
              label={
                attachment.via === 'bucket' &&
                attachment.kind === 'video' &&
                attachment.durationMs
                  ? `${attachment.name} · ${formatDuration(attachment.durationMs)}`
                  : attachment.name
              }
              size="small"
              // While uploading, the cross stops the upload; while the
              // message itself is sending, it is gone.
              onDelete={!sending || uploadPercent !== null ? removeAttachment : undefined}
              data-testid="attachment-chip"
            />
          </Box>
          {uploadPercent !== null && (
            <Box
              sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}
              data-testid="upload-progress"
            >
              <LinearProgress
                variant="determinate"
                value={uploadPercent}
                aria-label="Upload progress"
                sx={{ flex: 1, maxWidth: 240, borderRadius: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                Uploading {uploadPercent}%
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {showRow && (
        <Box
          ref={setRowEl}
          sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'flex-end' }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedTypes(bucket).join(',')}
            style={{ display: 'none' }}
            onChange={onFile}
            data-testid="attach-input"
          />
          {!isNote && (
            <Tooltip
              title={
                bucket
                  ? 'Attach a photo (PNG or JPEG, up to 10 MB) or a video (MP4 or MOV, up to 50 MB and 60 seconds)'
                  : 'Attach a photo (PNG or JPEG, up to 2 MB)'
              }
            >
              <span>
                <IconButton
                  aria-label={bucket ? 'Attach a photo or video' : 'Attach a photo'}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending || preparing}
                >
                  {preparing ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <AttachFileIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {!isNote && (
            <ActionsMenuButton
              count={actions.length}
              riderId={riderId}
              anchoredRentalId={anchoredRentalId}
              disabled={sending}
              onAdd={(action) => setActions((prev) => [...prev, action])}
            />
          )}
          <SavedReplyButton
            replies={savedReplies.replies}
            loading={savedReplies.loading}
            error={savedReplies.error}
            disabled={sending}
            onOpen={savedReplies.error ? savedReplies.reload : ensureReplies}
            onPick={(reply) => insertReply(reply, false)}
            onManage={() => setManageOpen(true)}
          />
          <TextField
            fullWidth
            multiline
            maxRows={6}
            size="small"
            value={input}
            inputRef={textRef}
            onChange={(e) => {
              setInput(e.target.value);
              // Searching saved replies ("/…") is not typing to the rider.
              if (
                !isNote &&
                e.target.value.trim() &&
                slashQuery(e.target.value) === null
              ) {
                onTyping?.();
              }
            }}
            onKeyDown={onKeyDown}
            disabled={sending}
            inputProps={{ 'aria-label': isNote ? 'Internal note' : 'Reply' }}
            placeholder={
              isNote
                ? 'Write an internal note'
                : attachment
                ? 'Add a caption (optional)'
                : 'Reply to the rider (Enter to send, / for saved replies)'
            }
            color={isNote ? 'warning' : 'primary'}
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
          />
          <Tooltip
            title={
              isNote
                ? 'Add note'
                : buttonsNeedText
                ? 'Add a message above the buttons'
                : 'Send reply'
            }
          >
            <span>
              <IconButton
                aria-label={isNote ? 'Add note' : 'Send reply'}
                onClick={send}
                disabled={!canSend}
                color={isNote ? 'warning' : 'secondary'}
              >
                {sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      <SlashReplyMenu
        query={slashOpen ? slash : null}
        anchorEl={rowEl}
        replies={savedReplies.replies}
        loading={savedReplies.loading}
        error={savedReplies.error}
        activeIndex={slashIndex}
        onHover={setSlashIndex}
        onPick={(reply) => insertReply(reply, true)}
      />

      <ManageSavedRepliesDialog
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        system={systemId ? { id: systemId, name: systemName } : null}
        canManageGlobal={canManageGlobal}
        onChanged={savedReplies.reload}
      />
    </Box>
  );
}

/**
 * In place of the reply on a chat another agent has: who, and Take over.
 * The composer hands it the focus when the chat changes hands as the agent
 * types, and it holds the keys that follow, all but Tab: none is typed,
 * none reaches the page's shortcuts (the page skips a key already handled),
 * and Enter does not press Take over.
 */
const HeldByBar = React.forwardRef<
  HTMLDivElement,
  { name: string; onTakeOver?: () => Promise<void> }
>(function HeldByBar({ name, onTakeOver }, ref) {
  const [busy, setBusy] = useState(false);
  return (
    <Stack
      ref={ref}
      tabIndex={-1}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Tab' || e.ctrlKey || e.metaKey || e.altKey) return;
        e.preventDefault();
      }}
      direction="row"
      alignItems="center"
      gap={2}
      sx={{ px: 2, py: 1.5 }}
      data-testid="held-by-bar"
    >
      <SupportAgentOutlinedIcon color="action" />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2">{name} is handling this chat</Typography>
        <Typography variant="caption" color="text.secondary">
          Take over to reply to the rider, or add an internal note.
        </Typography>
      </Box>
      {onTakeOver && (
        <Button
          variant="contained"
          disabled={busy}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
          onClick={async () => {
            setBusy(true);
            try {
              await onTakeOver();
            } catch (_) {
              // The page has said why.
            } finally {
              setBusy(false);
            }
          }}
        >
          Take over
        </Button>
      )}
    </Stack>
  );
});

/** The attachment's thumbnail: the photo, the video's poster, or a camera. */
function AttachmentThumb({ draft }: { draft: Draft }) {
  const src = draft.via === 'legacy' ? draft.dataUrl : draft.previewUrl;
  if (src) {
    return (
      <Box
        component="img"
        src={src}
        alt={draft.name}
        sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1 }}
      />
    );
  }
  return (
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: 1,
        bgcolor: 'action.hover',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'text.secondary',
      }}
      aria-label={draft.name}
      role="img"
    >
      <VideocamOutlinedIcon />
    </Box>
  );
}
